/**
 * 司机端设置页面
 * 修改密码、退出登录
 * 
 * 使用集中式API配置和DriverAPI服务
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL, DriverAPI, TokenManager, isProductionEnvironment, isPreviewEnvironment } from './config';
import DriverBrandHeader from './DriverBrandHeader';
import DriverLanguageDropdown from './DriverLanguageDropdown';
import { driverLanguages, getDriverLanguage, getDriverText, setDriverLanguagePreference } from './driverI18n';
import { getDriverFeatureText, getLocalizedDriverApiError } from './driverFeatureI18n';
import { callPhoneNumber } from '../../services/driverApp/phoneService';
import {
  authenticateDriverBiometric,
  isDriverBiometricAvailable,
  isDriverBiometricLoginEnabled,
  loadRememberedDriverCredentials,
  setDriverBiometricLoginEnabled,
} from '../../services/driverApp/credentialService';

const DRIVER_CUSTOMER_SERVICE_PHONE = '07036670926';
const DRIVER_CUSTOMER_SERVICE_WHATSAPP_PHONE = '817036670926';
const DRIVER_SETTINGS_LANGUAGE_ORDER = ['zh_cn', 'zh_tw', 'ja', 'en'];

export default function DriverSettings({ driver, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [removingDeviceId, setRemovingDeviceId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loginDevices, setLoginDevices] = useState([]);
  const [driverLanguage, setDriverLanguage] = useState(() => getDriverLanguage());
  const [notificationSaving, setNotificationSaving] = useState('');
  const [biometricLoginEnabled, setBiometricLoginEnabled] = useState(() => isDriverBiometricLoginEnabled());
  const [biometricSaving, setBiometricSaving] = useState(false);
  const [biometricFeedback, setBiometricFeedback] = useState({ type: '', message: '' });
  const [fleetCode, setFleetCode] = useState('');
  const [fleetJoining, setFleetJoining] = useState(false);
  const [showFleetExitModal, setShowFleetExitModal] = useState(false);
  const [fleetExitReason, setFleetExitReason] = useState('');
  const [fleetExitSubmitting, setFleetExitSubmitting] = useState(false);
  const [fleetExitRequest, setFleetExitRequest] = useState(() => driver?.fleet_exit_request || null);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');
  const [deleteAccountReason, setDeleteAccountReason] = useState('');
  const [deleteAccountAcknowledged, setDeleteAccountAcknowledged] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState('');
  const [notificationSettings, setNotificationSettings] = useState(() => ({
    new_reservation: driver?.notification_settings?.new_reservation !== false,
    reservation_changes: driver?.notification_settings?.reservation_changes !== false,
    urgent_contact: driver?.notification_settings?.urgent_contact !== false,
  }));
  const text = getDriverText(driverLanguage);
  const featureText = getDriverFeatureText(driverLanguage);
  const deletionText = featureText.accountDeletion;
  const orderedLanguageOptions = DRIVER_SETTINGS_LANGUAGE_ORDER
    .map((code) => driverLanguages.find((option) => option.code === code))
    .filter(Boolean);

  // 如果是首次登录强制修改密码
  const mustChangePassword = location.state?.mustChangePassword || driver?.must_change_password;

  useEffect(() => {
    console.log('[DriverSettings] API URL:', API_BASE_URL);
    if (mustChangePassword) {
      setShowPasswordModal(true);
    }
  }, [mustChangePassword]);

  useEffect(() => {
    setFleetExitRequest(driver?.fleet_exit_request || null);
  }, [driver?.fleet_exit_request]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 6) {
      setError(text.passwordTooShort);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(text.passwordMismatch);
      return;
    }

    setLoading(true);

    try {
      console.log('[DriverSettings] Changing password via DriverAPI...');
      
      // 使用集中式DriverAPI
      await DriverAPI.changePassword(currentPassword, newPassword);

      setSuccess(text.passwordChanged);
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // 更新本地存储的司机信息
      const driverInfo = TokenManager.getDriverInfo();
      if (driverInfo) {
        driverInfo.must_change_password = false;
        TokenManager.setDriverInfo(driverInfo);
      }

      // 如果是强制修改密码完成后，跳转到订单列表
      if (mustChangePassword) {
        setTimeout(() => navigate('/driver/orders'), 1500);
      }
    } catch (err) {
      if (err.message && err.message.includes('Safety Check Failed')) {
        setError(text.systemConfigError);
      } else {
        const message = getLocalizedDriverApiError(err, text.passwordChangeFailed, driverLanguage);
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleJoinFleet = async (event) => {
    event.preventDefault();
    if (!fleetCode.trim()) return;
    setFleetJoining(true); setError(''); setSuccess('');
    try {
      const result = await DriverAPI.joinFleet(fleetCode.trim().toUpperCase());
      setSuccess(text.fleetJoinSuccess);
      setFleetCode('');
      const info = TokenManager.getDriverInfo();
      if (info) TokenManager.setDriverInfo({ ...info, fleet_id: result.fleet?.id || 'joined' });
      window.setTimeout(() => window.location.reload(), 700);
    } catch (err) {
      setError(text.fleetJoinFailed);
    } finally { setFleetJoining(false); }
  };

  const handleFleetExitRequest = async (event) => {
    event.preventDefault();
    const reason = fleetExitReason.trim();
    if (!reason) return;
    setFleetExitSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const result = await DriverAPI.requestFleetExit(reason);
      const nextRequest = result.request || { reason, status: 'pending' };
      setFleetExitRequest(nextRequest);
      const info = TokenManager.getDriverInfo();
      if (info) TokenManager.setDriverInfo({ ...info, fleet_exit_request: nextRequest });
      setFleetExitReason('');
      setShowFleetExitModal(false);
      setSuccess(text.fleetExitRequestSuccess);
    } catch (err) {
      setError(getLocalizedDriverApiError(err, text.fleetExitRequestFailed, driverLanguage));
    } finally {
      setFleetExitSubmitting(false);
    }
  };

  const handleLogout = () => {
    TokenManager.clearAll();
    onLogout();
    navigate('/driver');
  };

  const formatLoginDeviceTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  const loadLoginDevices = async () => {
    setDevicesLoading(true);
    setError('');
    try {
      const result = await DriverAPI.getLoginDevices();
      setLoginDevices(Array.isArray(result.devices) ? result.devices : []);
    } catch (err) {
      setError(getLocalizedDriverApiError(err, text.loginDeviceLoadFailed, driverLanguage));
    } finally {
      setDevicesLoading(false);
    }
  };

  const openDeviceManagement = () => {
    setShowDeviceModal(true);
    loadLoginDevices();
  };

  const handleRemoveLoginDevice = async (device) => {
    if (!device?.id) return;
    setRemovingDeviceId(device.id);
    setError('');
    setSuccess('');
    try {
      const result = await DriverAPI.removeLoginDevice(device.id);
      setSuccess(text.loginDeviceRemoved);
      if (result.revoked_current || device.is_current) {
        TokenManager.clearAll();
        onLogout();
        navigate('/driver');
        return;
      }
      await loadLoginDevices();
    } catch (err) {
      setError(getLocalizedDriverApiError(err, text.loginDeviceRemoveFailed, driverLanguage));
    } finally {
      setRemovingDeviceId('');
    }
  };

  const openDriverCustomerServiceWhatsApp = () => {
    const message = [
      'Hello Big Taxi, I need driver support.',
      driver?.name ? `Driver: ${driver.name}` : '',
      driver?.phone ? `Phone: ${driver.phone}` : '',
    ].filter(Boolean).join('\n');
    const url = `https://wa.me/${DRIVER_CUSTOMER_SERVICE_WHATSAPP_PHONE}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCustomerServiceCall = () => {
    setShowPhoneModal(true);
  };

  const handleNotificationToggle = async (key) => {
    const nextSettings = {
      ...notificationSettings,
      [key]: !notificationSettings[key],
    };
    setNotificationSettings(nextSettings);
    setNotificationSaving(key);
    setError('');
    setSuccess('');
    try {
      const updatedDriver = await DriverAPI.updateNotificationSettings(nextSettings);
      TokenManager.setDriverInfo(updatedDriver);
      setSuccess(text.notificationSettingsSaved);
    } catch (err) {
      setNotificationSettings(notificationSettings);
      setError(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
    } finally {
      setNotificationSaving('');
    }
  };

  const handleLanguageChange = (language) => {
    const savedLanguage = setDriverLanguagePreference(language);
    setDriverLanguage(savedLanguage);
    setSuccess(getDriverText(savedLanguage).languageSaved);
    setError('');
  };

  const closeDeleteAccountModal = () => {
    if (deletingAccount) return;
    setShowDeleteAccountModal(false);
    setDeleteAccountPassword('');
    setDeleteAccountReason('');
    setDeleteAccountAcknowledged(false);
    setDeleteAccountError('');
  };

  const handleDeleteAccount = async (event) => {
    event.preventDefault();
    if (!deleteAccountPassword) {
      setDeleteAccountError(deletionText.passwordRequired);
      return;
    }
    if (!deleteAccountAcknowledged) {
      setDeleteAccountError(deletionText.acknowledgeRequired);
      return;
    }

    setDeletingAccount(true);
    setDeleteAccountError('');
    try {
      await DriverAPI.deleteAccount(deleteAccountPassword, deleteAccountReason.trim());
      TokenManager.clearAll();
      setShowDeleteAccountModal(false);
      onLogout();
      navigate('/driver', { replace: true });
    } catch (err) {
      setDeleteAccountError(
        Number(err?.response?.status) === 409
          ? deletionText.activeOrder
          : getLocalizedDriverApiError(err, deletionText.failed, driverLanguage)
      );
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleBiometricToggle = async () => {
    setError('');
    setSuccess('');
    setBiometricFeedback({ type: '', message: '' });

    if (biometricLoginEnabled) {
      setDriverBiometricLoginEnabled(false);
      setBiometricLoginEnabled(false);
      setSuccess(text.faceIdDisabled);
      setBiometricFeedback({ type: 'success', message: text.faceIdDisabled });
      return;
    }

    setBiometricSaving(true);
    setSuccess(text.faceIdChecking);
    setBiometricFeedback({ type: 'success', message: text.faceIdChecking });
    try {
      const availability = await isDriverBiometricAvailable();
      if (!availability.available) {
        setSuccess('');
        setError(text.faceIdUnavailable);
        setBiometricFeedback({ type: 'error', message: text.faceIdUnavailable });
        return;
      }

      await authenticateDriverBiometric(text.faceIdReason);
      const credentials = await loadRememberedDriverCredentials();
      if (!credentials.phone || !credentials.password) {
        setSuccess('');
        setError(text.faceIdCredentialRequired);
        setBiometricFeedback({ type: 'error', message: text.faceIdCredentialRequired });
        return;
      }

      setDriverBiometricLoginEnabled(true);
      setBiometricLoginEnabled(true);
      setSuccess(text.faceIdEnabled);
      setBiometricFeedback({ type: 'success', message: text.faceIdEnabled });
    } catch (err) {
      const message = err?.message === 'biometric_credentials_missing' ? text.faceIdCredentialRequired : text.faceIdAuthFailed;
      setSuccess('');
      setError(message);
      setBiometricFeedback({ type: 'error', message });
    } finally {
      setBiometricSaving(false);
    }
  };

  return (
    <div className="driver-settings-page min-h-screen bg-surface">
      <DriverBrandHeader pageTitle={text.settings} driverName={driver?.name} onBack={() => navigate('/driver/orders')} />

      <div className="driver-settings-shell">
        <section className="driver-settings-profile-card">
          <div className="driver-settings-profile-avatar" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 12.2a4.2 4.2 0 1 0 0-8.4 4.2 4.2 0 0 0 0 8.4Z" stroke="currentColor" strokeWidth="1.7" />
              <path d="M4.8 21a7.2 7.2 0 0 1 14.4 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </div>
          <div className="driver-settings-profile-copy">
            <strong>{driver?.name || text.driver}</strong>
            <span>{driver?.phone || ''}</span>
          </div>
          <span className="driver-settings-profile-shield" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 3.2 19 6v5.4c0 4.4-2.8 7.5-7 9.3-4.2-1.8-7-4.9-7-9.3V6l7-2.8Z" fill="currentColor" opacity="0.92" />
              <path d="m8.8 12 2.1 2.1 4.5-4.8" stroke="#0c0a07" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </section>

        {success && (
          <div className="premium-chip-success px-3 py-2 rounded-lg text-sm">
            {success}
          </div>
        )}

        {error && (
          <div className="premium-chip-destructive px-3 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}

        {!driver?.fleet_id && (
          <section className="driver-settings-panel">
            <h2>{text.fleetJoinTitle}</h2>
            <p className="mb-3 text-sm text-muted-foreground">{text.fleetJoinHint}</p>
            <form className="flex gap-2" onSubmit={handleJoinFleet}>
              <input className="premium-input min-w-0 flex-1" value={fleetCode} onChange={(event) => setFleetCode(event.target.value.toUpperCase())} placeholder={text.fleetCodePlaceholder} />
              <button className="premium-button bg-primary px-3 text-primary-foreground disabled:opacity-50" disabled={fleetJoining}>{fleetJoining ? text.fleetJoining : text.fleetJoin}</button>
            </form>
          </section>
        )}

        {driver?.fleet_id && !driver?.is_fleet_master && (
          <section className="driver-settings-panel" data-testid="fleet-membership-panel">
            <h2>{text.fleetMembershipTitle}</h2>
            <div className="driver-settings-list-row">
              <span className="driver-settings-row-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none"><path d="M5 20V9.5L12 4l7 5.5V20M3.5 20h17M8 20v-6h8v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
              <span className="driver-settings-toggle-copy"><strong>{driver?.fleet?.fleet_name || text.fleetMembershipUnknown}</strong><small>{text.fleetMembershipHint}</small></span>
            </div>
            {fleetExitRequest?.status === 'pending' ? (
              <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-3 text-sm text-amber-100" data-testid="fleet-exit-request-pending">
                <strong className="block text-amber-200">{text.fleetExitRequestPending}</strong>
                {fleetExitRequest.reason && <span className="mt-1 block text-xs leading-5 text-muted-foreground">{fleetExitRequest.reason}</span>}
              </div>
            ) : (
              <button type="button" className="rounded-xl border border-destructive/35 bg-destructive/10 px-3 py-3 text-sm font-bold text-destructive" onClick={() => setShowFleetExitModal(true)} data-testid="fleet-exit-request-button">
                {text.fleetExitRequest}
              </button>
            )}
          </section>
        )}

        {driver?.is_fleet_master && (
          <section className="driver-settings-panel" data-testid="fleet-management-entry">
            <h2>{text.fleetManagementTitle}</h2>
            <button type="button" className="driver-settings-list-row" onClick={() => navigate('/fleet/manage?tab=drivers')}>
              <span className="driver-settings-row-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M5 20V9.5L12 4l7 5.5V20M3.5 20h17M8 20v-6h8v6M8.5 10h.01M12 10h.01M15.5 10h.01" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
              <span className="driver-settings-toggle-copy"><strong>{text.fleetManagementTitle}</strong><small>{text.fleetManagementHint}</small></span>
              <span className="driver-settings-chevron">›</span>
            </button>
          </section>
        )}

        <section className="driver-settings-panel driver-settings-emergency-support-panel">
          <h2>{text.emergencyContact}</h2>
          <button
            type="button"
            onClick={openDriverCustomerServiceWhatsApp}
            className="driver-settings-list-row driver-settings-list-row-danger driver-settings-support-row"
            data-testid="driver-settings-whatsapp-support"
          >
            <span className="driver-settings-row-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M6.5 17.5v-5.2a5.5 5.5 0 0 1 11 0v5.2M6.5 16h-1A2.5 2.5 0 0 1 3 13.5v-1A2.5 2.5 0 0 1 5.5 10H7v6Zm11 0h1a2.5 2.5 0 0 0 2.5-2.5v-1a2.5 2.5 0 0 0-2.5-2.5H17v6Zm0 0c0 2.2-1.4 3.5-3.6 3.5H12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </span>
            <span>{text.onlineBigTaxiSupport}</span>
            <span className="driver-settings-chevron">›</span>
          </button>
          <button
            type="button"
            onClick={handleCustomerServiceCall}
            className="driver-settings-list-row driver-settings-support-row"
            data-testid="driver-settings-phone-support"
          >
            <span className="driver-settings-row-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M6.8 4.5 9 3.8l2.2 4.7-1.5 1.3a10.4 10.4 0 0 0 4.6 4.6l1.3-1.5 4.7 2.2-.7 2.2c-.3.9-1.1 1.5-2.1 1.4C10.8 18.1 5.9 13.2 5.3 6.5c-.1-1 .5-1.8 1.5-2Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span>{text.phoneBigTaxiSupport}</span>
            <span className="driver-settings-chevron">›</span>
          </button>
        </section>

        <section className="driver-settings-panel">
          <h2>{text.notificationSettings}</h2>
          {[
            ['new_reservation', text.newReservationNotification],
            ['reservation_changes', text.reservationChangeNotification],
            ['urgent_contact', text.urgentContactNotification],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => handleNotificationToggle(key)}
              className="driver-settings-toggle-row"
              disabled={notificationSaving === key}
              data-testid={`driver-notification-${key}`}
            >
              <span className="driver-settings-row-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M18 10a6 6 0 1 0-12 0c0 5-2 5.5-2 7h16c0-1.5-2-2-2-7Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                  <path d="M9.8 20a2.3 2.3 0 0 0 4.4 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
              </span>
              <span>{label}</span>
              <span className={`driver-settings-switch ${notificationSettings[key] ? 'is-on' : ''}`}>
                <span />
              </span>
            </button>
          ))}
          <button
            type="button"
            onClick={openDeviceManagement}
            className="driver-settings-list-row"
            data-testid="driver-settings-device-management-button"
          >
            <span className="driver-settings-row-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M8 3.5h8A1.5 1.5 0 0 1 17.5 5v14A1.5 1.5 0 0 1 16 20.5H8A1.5 1.5 0 0 1 6.5 19V5A1.5 1.5 0 0 1 8 3.5Z" stroke="currentColor" strokeWidth="1.7" />
                <path d="M11 17.5h2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </span>
            <span>{text.deviceManagement}</span>
            <span className="driver-settings-chevron">›</span>
          </button>
          <button
            type="button"
            onClick={() => setShowPasswordModal(true)}
            className="driver-settings-list-row"
            data-testid="change-password-button"
          >
            <span className="driver-settings-row-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M7 10V8a5 5 0 0 1 10 0v2M6.5 10h11A1.5 1.5 0 0 1 19 11.5v8A1.5 1.5 0 0 1 17.5 21h-11A1.5 1.5 0 0 1 5 19.5v-8A1.5 1.5 0 0 1 6.5 10Z" stroke="currentColor" strokeWidth="1.7" />
                <path d="M12 15v2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </span>
            <span>{text.changePassword}</span>
            <span className="driver-settings-chevron">›</span>
          </button>
          <button
            type="button"
            onClick={handleBiometricToggle}
            className={`driver-settings-toggle-row driver-settings-face-id-toggle ${biometricSaving ? 'is-checking' : ''}`}
            disabled={biometricSaving}
            data-testid="driver-settings-face-id-login"
          >
            <span className="driver-settings-row-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M8 3H6.5A2.5 2.5 0 0 0 4 5.5V7M16 3h1.5A2.5 2.5 0 0 1 20 5.5V7M8 21H6.5A2.5 2.5 0 0 1 4 18.5V17M16 21h1.5a2.5 2.5 0 0 0 2.5-2.5V17" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                <path d="M9 9.2v1.1M15 9.2v1.1M8.7 15.1c1.8 1.7 4.8 1.7 6.6 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </span>
            <span className="driver-settings-toggle-copy">
              <strong>{text.faceIdLogin}</strong>
              <small>{text.faceIdLoginHint}</small>
            </span>
            <span className={`driver-settings-switch ${biometricLoginEnabled ? 'is-on' : ''} ${biometricSaving ? 'is-checking' : ''}`}>
              <span />
            </span>
          </button>
          {biometricFeedback.message && (
            <div
              className={`driver-settings-face-id-feedback ${
                biometricFeedback.type === 'error' ? 'is-error' : 'is-success'
              }`}
              aria-live="polite"
              data-testid="driver-settings-face-id-feedback"
            >
              {biometricFeedback.message}
            </div>
          )}
          <button
            type="button"
            onClick={handleLogout}
            className="driver-settings-list-row driver-settings-logout-row"
            data-testid="logout-button"
          >
            <span>{text.logout}</span>
            <span className="driver-settings-chevron">›</span>
          </button>
        </section>

        <section className="driver-settings-panel">
          <h2>{text.languageSettings}</h2>
          <DriverLanguageDropdown
            options={orderedLanguageOptions}
            value={driverLanguage}
            onChange={handleLanguageChange}
            ariaLabel={text.languageSettings}
            testId="driver-settings-language-dropdown"
            className="driver-settings-language-dropdown"
          />
          <p className="driver-settings-language-hint">{text.languageSettingsHint}</p>
        </section>

        <section className="driver-settings-panel" data-testid="driver-account-management-section">
          <h2>{deletionText.sectionTitle}</h2>
          <button
            type="button"
            onClick={() => setShowDeleteAccountModal(true)}
            className="driver-settings-list-row driver-settings-list-row-danger"
            data-testid="driver-delete-account-button"
          >
            <span className="driver-settings-row-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M4.5 7h15M9 7V4.5h6V7m-8 0 .7 13h8.6L17 7M10 10.5v6M14 10.5v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span>{deletionText.button}</span>
            <span className="driver-settings-chevron">›</span>
          </button>
        </section>

        <section className="driver-settings-panel driver-settings-about-panel">
          <div className="driver-settings-info-row" data-testid="driver-settings-about-app">
            <span>{text.aboutApp}</span>
            <strong>{text.versionNumber}: {text.driverAppVersion}</strong>
          </div>
        </section>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="premium-card w-full max-w-sm">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">
                {mustChangePassword ? text.firstLoginChangePassword : text.changePassword}
              </h3>
              {!mustChangePassword && (
                <button 
                  onClick={() => setShowPasswordModal(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            <form onSubmit={handleChangePassword} className="p-4 space-y-4">
              {error && (
                <div className="premium-chip-destructive px-3 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {text.currentPassword}
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="premium-input"
                  placeholder={text.currentPasswordPlaceholder}
                  required
                  data-testid="current-password-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {text.newPassword}
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="premium-input"
                  placeholder={text.newPasswordPlaceholder}
                  required
                  data-testid="new-password-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {text.confirmNewPassword}
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="premium-input"
                  placeholder={text.confirmPasswordPlaceholder}
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full premium-button bg-primary text-primary-foreground py-3 disabled:opacity-50 transition"
                data-testid="submit-password-change"
              >
                {loading ? text.submitting : text.submitConfirm}
              </button>
            </form>
          </div>
        </div>,
        document.body
      )}

      {showDeviceModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="premium-card w-full max-w-md max-h-[86vh] flex flex-col">
            <div className="p-4 border-b flex justify-between items-center gap-4">
              <div>
                <h3 className="text-lg font-bold">{text.loginDeviceManagement}</h3>
                <div className="mt-1 text-xs text-muted-foreground">{text.loginDeviceManagementHint}</div>
              </div>
              <button
                type="button"
                onClick={() => setShowDeviceModal(false)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={text.close}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              {error && (
                <div className="mb-3 premium-chip-destructive px-3 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}
              {devicesLoading ? (
                <div className="py-8 text-center text-sm text-muted-foreground">{text.loading}</div>
              ) : loginDevices.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">{text.loginDevicesEmpty}</div>
              ) : (
                <div className="space-y-3">
                  {loginDevices.map((device) => (
                    <div
                      key={device.id}
                      className="rounded-2xl border border-border-subtle bg-surface/60 p-4"
                      data-testid="driver-login-device-row"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-semibold text-foreground break-words">
                              {device.device_name || device.platform || 'Device'}
                            </div>
                            {device.is_current && (
                              <span className="premium-chip-success px-2 py-1 rounded-full text-xs">
                                {text.currentDevice}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {[device.platform, device.operating_system, device.app_version].filter(Boolean).join(' / ')}
                          </div>
                          <div className="mt-2 text-xs text-muted-foreground">
                            {text.lastLoginAt}: {formatLoginDeviceTime(device.last_login_at)}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveLoginDevice(device)}
                          className="premium-button bg-destructive/10 text-destructive px-3 py-2 text-sm disabled:opacity-50"
                          disabled={removingDeviceId === device.id}
                          data-testid="driver-remove-login-device-button"
                        >
                          {removingDeviceId === device.id ? text.removingDevice : text.removeDevice}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showPhoneModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="premium-card w-full max-w-sm">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">{text.phoneBigTaxiSupport}</h3>
              <button
                type="button"
                onClick={() => setShowPhoneModal(false)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={text.close}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-sm text-muted-foreground">{text.callBigTaxiSupportPrompt}</div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setShowPhoneModal(false)}
                  className="premium-button bg-muted text-foreground py-3"
                >
                  {text.cancel}
                </button>
                <button
                  type="button"
                  onClick={() => callPhoneNumber(DRIVER_CUSTOMER_SERVICE_PHONE)}
                  className="premium-button bg-primary text-primary-foreground py-3"
                  data-testid="driver-settings-confirm-call-support"
                >
                  {text.callNow}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showFleetExitModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-end bg-black/70 p-4">
          <form className="premium-card w-full max-w-md space-y-4 rounded-2xl p-5" onSubmit={handleFleetExitRequest} role="dialog" aria-modal="true" aria-label={text.fleetExitRequest}>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">{text.fleetMembershipLabel}</p>
              <h3 className="mt-1 text-lg font-bold">{text.fleetExitRequest}</h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{text.fleetExitRequestHint}</p>
            </div>
            <textarea required autoFocus rows={4} className="premium-input min-h-28 w-full resize-none" value={fleetExitReason} onChange={(event) => setFleetExitReason(event.target.value)} placeholder={text.fleetExitReasonPlaceholder} />
            <div className="grid grid-cols-2 gap-3">
              <button type="button" className="premium-chip py-3" onClick={() => { setShowFleetExitModal(false); setFleetExitReason(''); }} disabled={fleetExitSubmitting}>{text.cancel}</button>
              <button className="premium-button bg-primary py-3 text-primary-foreground disabled:opacity-50" disabled={fleetExitSubmitting || !fleetExitReason.trim()}>{fleetExitSubmitting ? text.fleetExitSubmitting : text.submitConfirm}</button>
            </div>
          </form>
        </div>,
        document.body
      )}

      {showDeleteAccountModal && createPortal(
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/75 p-4" role="presentation">
          <form
            className="premium-card max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto p-5"
            onSubmit={handleDeleteAccount}
            role="dialog"
            aria-modal="true"
            aria-labelledby="driver-delete-account-title"
            data-testid="driver-delete-account-dialog"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 id="driver-delete-account-title" className="text-lg font-bold text-destructive">{deletionText.title}</h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{deletionText.warning}</p>
              </div>
              <button type="button" onClick={closeDeleteAccountModal} className="text-2xl text-muted-foreground" aria-label={text.close}>×</button>
            </div>
            {driver?.is_fleet_master && (
              <div className="mt-4 rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-sm leading-6 text-amber-100">
                {deletionText.fleetWarning}
              </div>
            )}
            {deleteAccountError && (
              <div className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive" role="alert">
                {deleteAccountError}
              </div>
            )}
            <label className="mt-4 block text-sm font-medium">
              <span className="mb-1 block">{deletionText.password}</span>
              <input
                type="password"
                autoComplete="current-password"
                className="premium-input"
                value={deleteAccountPassword}
                onChange={(event) => setDeleteAccountPassword(event.target.value)}
                placeholder={deletionText.passwordPlaceholder}
                data-testid="driver-delete-account-password"
              />
            </label>
            <label className="mt-4 block text-sm font-medium">
              <span className="mb-1 block">{deletionText.reason}</span>
              <textarea
                className="premium-input min-h-24 resize-none"
                value={deleteAccountReason}
                onChange={(event) => setDeleteAccountReason(event.target.value)}
                placeholder={deletionText.reasonPlaceholder}
              />
            </label>
            <label className="mt-4 flex items-start gap-3 rounded-xl border border-border-subtle bg-surface p-3 text-sm leading-6">
              <input
                type="checkbox"
                className="mt-1 h-5 w-5 shrink-0 accent-[#d8b769]"
                checked={deleteAccountAcknowledged}
                onChange={(event) => setDeleteAccountAcknowledged(event.target.checked)}
                data-testid="driver-delete-account-acknowledge"
              />
              <span>{deletionText.acknowledge}</span>
            </label>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button type="button" className="premium-chip py-3" onClick={closeDeleteAccountModal} disabled={deletingAccount}>{deletionText.cancel}</button>
              <button
                type="submit"
                className="rounded-xl border border-destructive/40 bg-destructive/15 py-3 font-bold text-destructive disabled:opacity-45"
                disabled={deletingAccount}
                data-testid="driver-delete-account-confirm"
              >
                {deletingAccount ? deletionText.deleting : deletionText.confirm}
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}

    </div>
  );
}
